/**
 * Product-list filter state and URL-param encoding.
 * Lives outside the Relewise integration because the filter URL contract is owned
 * by the PLP UI, not by any particular search backend.
 */

import type { Gender } from '../shared/services/gender-cookie-service/gender-cookie-service.types';

export interface FilterOptions {
    gender?: Gender;
    priceRange?: { min: number; max: number };
    categoryIds?: string[];
    sizes?: string[];
    colors?: string[];
}

/** URL param keys that encode `FilterOptions`. Used when rewriting filter state in the URL. */
export const FILTER_PARAM_KEYS = ['gender', 'priceMin', 'priceMax', 'category', 'size', 'color'] as const;

/**
 * Reads `FilterOptions` from URL query parameters using the provided getter.
 * Returns undefined when no filter params are present. Safe for SSR: never throws.
 *
 * Encoding scheme (plain URL params, unicode-safe):
 *   priceMin, priceMax   — numeric range
 *   category, size, color — comma-separated tokens
 */
export function parseFilterOptions(getParam: (key: string) => string | null | undefined): FilterOptions | undefined {
    const result: FilterOptions = {};

    const gender = getParam('gender');
    if (gender === 'female' || gender === 'male') {
        result.gender = gender;
    }

    const priceMin = getParam('priceMin');
    const priceMax = getParam('priceMax');
    if (priceMin !== null && priceMin !== undefined && priceMax !== null && priceMax !== undefined) {
        const min = Number(priceMin);
        const max = Number(priceMax);
        if (Number.isFinite(min) && Number.isFinite(max)) {
            result.priceRange = { min, max };
        }
    }

    const splitCsv = (value: string | null | undefined): string[] | undefined => {
        if (!value) {
            return undefined;
        }
        const parts = value.split(',').filter(Boolean);
        return parts.length ? parts : undefined;
    };

    const categoryIds = splitCsv(getParam('category'));
    if (categoryIds) {
        result.categoryIds = categoryIds;
    }
    const sizes = splitCsv(getParam('size'));
    if (sizes) {
        result.sizes = sizes;
    }
    const colors = splitCsv(getParam('color'));
    if (colors) {
        result.colors = colors;
    }

    return Object.keys(result).length ? result : undefined;
}

/**
 * Mutates the given URLSearchParams in place to reflect the provided FilterOptions,
 * clearing any previous filter params first.
 */
export function writeFilterOptionsToParams(params: URLSearchParams, filterOptions: FilterOptions): void {
    for (const key of FILTER_PARAM_KEYS) {
        params.delete(key);
    }
    if (filterOptions.gender) {
        params.set('gender', filterOptions.gender);
    }
    if (filterOptions.priceRange) {
        params.set('priceMin', String(filterOptions.priceRange.min));
        params.set('priceMax', String(filterOptions.priceRange.max));
    }
    if (filterOptions.categoryIds?.length) {
        params.set('category', filterOptions.categoryIds.join(','));
    }
    if (filterOptions.sizes?.length) {
        params.set('size', filterOptions.sizes.join(','));
    }
    if (filterOptions.colors?.length) {
        params.set('color', filterOptions.colors.join(','));
    }
}
